import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { Analytics, Bugsnag, CustomEventTypes, Device } from '@src/config'
import { AppStackParamList } from '@src/navigation/types'
import {
  healthConnectPermissionDarkImage,
  healthConnectPermissionLightImage,
  healthKitPermissionDarkImage,
  healthKitPermissionLightImage,
} from '@src/assets/images'
import { useIsDarkMode } from '@src/config/theme'
import { useTerraAppleHealth, useTerraHealthConnect } from '@src/screens/Settings/utils/hooks'
import { ListProviders, TerraProviderKind } from '@src/types'
import { useDispatchAsync, useSnack } from '@src/utils'

const HEALTH_CONNECT_PROVIDER_LOAD_ERROR = 'Unable to load Health Connect. Please try again.'

export const useHealthPermissionScreenProps = ({ onComplete }: { onComplete: () => void }) => {
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const initTerraAppleHealthConnection = useTerraAppleHealth()
  const initTerraHealthConnect = useTerraHealthConnect()

  let sourceType: string
  let title: string
  let images: { light: any; dark: any }

  if (Device.ios) {
    sourceType = 'Apple Health'
    title = 'Sync with Apple Health'
    images = { light: healthKitPermissionLightImage, dark: healthKitPermissionDarkImage }
  } else {
    sourceType = 'Health Connect'
    title = 'Sync with Health Connect'
    images = { light: healthConnectPermissionLightImage, dark: healthConnectPermissionDarkImage }
  }

  const isDarkMode = useIsDarkMode()
  const imageSource = isDarkMode ? images.dark.imageSource : images.light.imageSource

  // this function assumes it is called from the last slide in the carousel
  // when we navigate to terra provider screen we replace the current screen
  // and going back from terra provider screen will go back to the next correct screen in the stack
  const onAllowButtonPress = async () => {
    if (Device.ios) {
      await initTerraAppleHealthConnection()

      Analytics.track(CustomEventTypes.OnboardingHealthKitSyncEnabled, {
        source: 'Onboarding Permissions',
      })

      onComplete()

      return
    }

    try {
      const response = await dispatchAsync<ListProviders>({
        type: 'settings/fetchTerraProviders',
      })
      const healthConnectProvider = response.providers.find(
        (provider) => provider.providerKind === TerraProviderKind.HealthConnect,
      )

      if (!healthConnectProvider) {
        Bugsnag.notify('Health Connect provider is unavailable after fetching Terra providers')
        showSnack(HEALTH_CONNECT_PROVIDER_LOAD_ERROR, null, 'error')
        return
      }

      await initTerraHealthConnect()

      return navigation.navigate('TerraProvider', {
        provider: healthConnectProvider.provider,
      })
    } catch (error: any) {
      Bugsnag.notify(error)
      showSnack(HEALTH_CONNECT_PROVIDER_LOAD_ERROR, null, 'error')
    }
  }

  return {
    sourceType,
    title,
    imageSource,
    allowButtonText: 'Continue',
    onAllowButtonPress,
  }
}
